import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { prefersReducedMotion } from '@/lib/motion';
import { useMediaQuery } from '@/lib/use-media-query';
import { cn } from '@/lib/utils';

export interface RailTrackProps {
  /** Names the scroll region (`role="group"`). */
  label: string;
  /** Translated aria-labels for the prev/next arrows — the caller owns i18n. */
  scrollLeftLabel: string;
  scrollRightLabel: string;
  children: ReactNode;
  className?: string;
  /** Extra classes on the scrolling track (gap, padding). */
  trackClassName?: string;
}

/**
 * The horizontal-row mechanics (ui-ux.md §3) for rails whose items are not
 * plain `PosterRow` covers (Tonight's "Also in progress", the Library's Up
 * next rail): a `scrollbar-none` snap track, the `mask-image` edge fade on
 * whichever side still has content, one `role="group"` tab stop with
 * ArrowLeft/ArrowRight scrolling a viewport-width, and prev/next arrows that
 * fade in on row hover on `(hover: hover) and (pointer: fine)` viewports only. Children are the
 * items — give each `shrink-0 snap-start` and a fixed width.
 */
export function RailTrack({
  label,
  scrollLeftLabel,
  scrollRightLabel,
  children,
  className,
  trackClassName,
}: RailTrackProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const [edges, setEdges] = useState({ left: false, right: false });

  // Track-only scroll metrics (no getBoundingClientRect); the fade follows
  // whichever edge still has content to reveal.
  const updateEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setEdges((prev) => {
      const next = {
        left: el.scrollLeft > 1,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      };
      return prev.left === next.left && prev.right === next.right ? prev : next;
    });
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, [updateEdges]);

  // Items coming and going change the scroll width without a scroll event;
  // the setter bails out when nothing changed, so this is cheap per render.
  useEffect(updateEdges);

  const scrollByPage = (direction: 1 | -1) => {
    trackRef.current?.scrollBy?.({
      left: direction * trackRef.current.clientWidth,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Only when the track itself has focus — arrows inside an item's link or
    // button keep their native meaning.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollByPage(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollByPage(1);
    }
  };

  const maskClass =
    edges.left && edges.right
      ? '[mask-image:linear-gradient(to_right,transparent_0,black_24px,black_calc(100%_-_24px),transparent_100%)]'
      : edges.right
        ? '[mask-image:linear-gradient(to_right,black_0,black_calc(100%_-_24px),transparent_100%)]'
        : edges.left
          ? '[mask-image:linear-gradient(to_left,black_0,black_calc(100%_-_24px),transparent_100%)]'
          : undefined;

  const arrow = (side: 'left' | 'right') => (
    <div
      className={cn(
        'absolute top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-fast focus-within:opacity-100',
        side === 'left' ? 'left-2' : 'right-2',
        edges[side] ? 'group-hover/rail:opacity-100' : 'pointer-events-none',
      )}
    >
      <Button
        variant="neutral"
        size="icon"
        aria-label={side === 'left' ? scrollLeftLabel : scrollRightLabel}
        onClick={() => scrollByPage(side === 'left' ? -1 : 1)}
      >
        {side === 'left' ? (
          <ChevronLeft className="size-5" strokeWidth={1.75} aria-hidden />
        ) : (
          <ChevronRight className="size-5" strokeWidth={1.75} aria-hidden />
        )}
      </Button>
    </div>
  );

  return (
    <div className={cn('group/rail relative', className)}>
      <div
        ref={trackRef}
        role="group"
        aria-label={label}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={cn(
          'scrollbar-none flex snap-x snap-mandatory gap-4 overflow-x-auto',
          maskClass,
          trackClassName,
        )}
      >
        {children}
      </div>
      {canHover ? (
        <>
          {arrow('left')}
          {arrow('right')}
        </>
      ) : null}
    </div>
  );
}
